/**
 * CompetencySetFormModal Component
 * Modal for creating/editing competency set name and description
 */

import React, { useState, useEffect } from "react";
import {
  useCreateCompetencySetMutation,
  useUpdateCompetencySetMutation,
} from "../../../services/api";

export default function CompetencySetFormModal({ competencySet, onClose }) {
  const isEditing = !!competencySet;

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    isActive: true,
  });
  const [error, setError] = useState("");

  // API hooks
  const [createSet, { isLoading: isCreating }] = useCreateCompetencySetMutation();
  const [updateSet, { isLoading: isUpdating }] = useUpdateCompetencySetMutation();

  const isLoading = isCreating || isUpdating;

  // Initialize form data when editing
  useEffect(() => {
    if (competencySet) {
      setFormData({
        name: competencySet.name || "",
        description: competencySet.description || "",
        isActive: competencySet.isActive !== undefined ? competencySet.isActive : true,
      });
    }
  }, [competencySet]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }

    try {
      if (isEditing) {
        await updateSet({
          id: competencySet._id,
          name: formData.name.trim(),
          description: formData.description.trim(),
          isActive: formData.isActive,
        }).unwrap();
      } else {
        await createSet({
          name: formData.name.trim(),
          description: formData.description.trim(),
          isActive: formData.isActive,
          values: [], // Start with empty values
        }).unwrap();
      }
      onClose();
    } catch (err) {
      console.error("Failed to save competency set:", err);
      setError(err?.data?.message || "Failed to save. Please try again.");
    }
  };

  return (
    <div className="cd-modal-overlay" onClick={onClose}>
      <div className="cd-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="cd-modal-header">
          <h2 className="cd-modal-title">
            {isEditing ? "Edit Competency Set" : "Create Competency Set"}
          </h2>
          <button className="cd-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="cd-modal-body">
            {error && <div className="cd-form-error">{error}</div>}

            <div className="cd-form-group">
              <label htmlFor="cs-name">Name *</label>
              <input
                type="text"
                id="cs-name"
                className="cd-input"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="e.g., Leadership Competencies 2025"
                autoFocus
              />
            </div>

            <div className="cd-form-group">
              <label htmlFor="cs-description">Description</label>
              <textarea
                id="cs-description"
                className="cd-input cd-textarea"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Brief description of this competency set..."
                rows={3}
              />
            </div>

            <div className="cd-form-group cd-checkbox-group">
              <label className="cd-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleChange("isActive", e.target.checked)}
                />
                <span>Active</span>
              </label>
              <span className="cd-checkbox-hint">
                Inactive sets won't be visible to assigned companies
              </span>
            </div>
          </div>

          <div className="cd-modal-footer">
            <button
              type="button"
              className="cd-btn cd-btn-secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="cd-btn cd-btn-primary"
              disabled={isLoading || !formData.name.trim()}
            >
              {isLoading ? "Saving..." : isEditing ? "Save Changes" : "Create"}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .cd-textarea {
          resize: vertical;
          min-height: 80px;
        }

        .cd-form-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          padding: 10px 12px;
          border-radius: 8px;
          font-size: 13px;
          margin-bottom: 16px;
        }

        .cd-checkbox-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .cd-checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 14px;
        }

        .cd-checkbox-label input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
        }

        .cd-checkbox-hint {
          font-size: 12px;
          color: #6b7280;
          margin-left: 24px;
        }

        .cd-modal-content {
          max-width: 480px;
        }
      `}</style>
    </div>
  );
}
